import { useMemo } from 'react'
import { Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import ApartmentIcon from '@mui/icons-material/Apartment'
import VideocamIcon from '@mui/icons-material/Videocam'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import {
  Body,
  BookButton,
  BottomContainer,
  CardContainer,
  MainInfo,
  SlotOption,
  Slots,
} from './styles'
import {
  buildApptURL,
  buildDirectionsLink,
  formatPhone,
  formatSlot,
} from '../../../utils/helpers'

type ListCardProps = {
  id: string
  name: string
  isPediatrics: boolean
  image: string
  address: string
  city: string
  zip: string
  phoneNumber: string
  latitude: number
  longitude: number
  distance: number | null
  videoSlot: Date | null
  officeSlot: Date | null
  locationUrl: string
  onClick: (lat: number, lng: number) => void
}

const LocationCardList = ({
  id,
  name,
  image,
  isPediatrics,
  address,
  city,
  zip,
  phoneNumber,
  latitude,
  longitude,
  distance,
  videoSlot,
  officeSlot,
  locationUrl,
  onClick,
}: ListCardProps): JSX.Element => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down(575))
  const formattedVideoSlot = useMemo(() => {
    if (!videoSlot) return null
    return formatSlot(videoSlot)
  }, [videoSlot])

  const formattedOfficeSlot = useMemo(() => {
    if (!officeSlot) return null
    return formatSlot(officeSlot)
  }, [officeSlot])

  return (
    <CardContainer onClick={() => onClick(latitude, longitude)}>
      <div>
        <MainInfo>
          <div>
            <img src={image} alt="location-building" />
            {isMobile && (
              <Typography
                fontWeight={600}
                color="primary"
                fontSize={24}
                lineHeight={1.2}
              >
                {name}
              </Typography>
            )}
          </div>
          <div>
            {!isMobile && (
              <Typography
                fontWeight={600}
                color="primary"
                fontSize={24}
                lineHeight={1.2}
              >
                {name}
              </Typography>
            )}
            <Body>
              <div className="information">
                <Typography fontSize={14} lineHeight={1.3} fontWeight={500}>
                  {address}
                </Typography>
                <Typography fontSize={14} lineHeight={1.3} fontWeight={500}>
                  {city}, {zip}
                </Typography>
                <Typography fontSize={14} fontWeight={500}>
                  <a href={`tel:+1${phoneNumber}`}>
                    {formatPhone(phoneNumber)}
                  </a>
                </Typography>
              </div>
              <Slots>
                <Tooltip title="Video visit" placement="top" arrow>
                  <SlotOption available={+Boolean(formattedVideoSlot)}>
                    <div>
                      <VideocamIcon fontSize="small" />
                    </div>
                    <Typography fontWeight={600}>
                      {formattedVideoSlot || 'Not available'}
                    </Typography>
                  </SlotOption>
                </Tooltip>
                <Tooltip title="In-Office visit" placement="bottom" arrow>
                  <SlotOption available={+Boolean(formattedOfficeSlot)}>
                    <div>
                      <ApartmentIcon fontSize="small" />
                    </div>
                    <Typography fontWeight={600}>
                      {formattedOfficeSlot || 'Not available'}
                    </Typography>
                  </SlotOption>
                </Tooltip>
              </Slots>
            </Body>
          </div>
        </MainInfo>
      </div>
      <BottomContainer>
        <div>
          <PlaceOutlinedIcon color="primary" fontSize="small" />
          <Typography
            href={buildDirectionsLink(address, city, zip)}
            target="_blank"
            rel="noreferrer"
            component="a"
          >
            Directions
          </Typography>
          <Typography
            className="separator"
            color="primary"
            fontWeight={600}
            component="span"
          >
            ·
          </Typography>
          <Typography
            href={locationUrl}
            target="_blank"
            rel="noreferrer"
            component="a"
          >
            More Info
          </Typography>
          {distance && (
            <>
              <Typography
                className="separator"
                color="primary"
                fontWeight={600}
                component="span"
              >
                ·
              </Typography>
              <Typography component="span">
                {distance.toFixed(2)} miles away
              </Typography>
            </>
          )}
        </div>
        <a href={buildApptURL(id, isPediatrics)}>
          <BookButton variant="outlined" size="small" color="primary" sx={ { color: theme.palette.backgroundMain, borderColor: theme.palette.backgroundMain }}>
            Book Appointment
          </BookButton>
        </a>
      </BottomContainer>
    </CardContainer>
  )
}

export default LocationCardList
